import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ENTITY_TYPES,
  INPUT_TYPES,
  PLATFORM_MODULES_OPTIONS,
} from "@miplatform/constants";
import {
  Colors,
  EmailTemplateType,
  StepDisplayConfigButtonType,
  StepType,
} from "@miplatform/types";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Collapse, Divider, Typography } from "@mui/material";

const templateItemSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  priorityOrder: z.coerce.number().optional(),
  areaName: z.string().min(1, "Area Name is required"),
  canBeNotApplicable: z.boolean().optional(),
  isRequired: z.boolean().optional(),
  requiresPhoto: z.boolean().optional(),
  requiresVideo: z.boolean().optional(),
  displayConfigSwitch: z.boolean().optional(),
  documentCategoryId: z.any().optional(),
  emailType: z.any().optional(),
  stepType: z.nativeEnum(StepType),
  module: z.string().min(1, "Module is required"),
});

const displayConfigSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  hasButton: z.boolean().optional(),
});

const displayButtonSchema = z.object({
  buttonType: z.string().min(1, "Button Type is required"),
  buttonLabel: z.string().min(1, "Button Label is required"),
  content: z.string().min(1, "Content is required"),
});

export const StepTemplateModal = ({
  open,
  onClose,
  onSubmit,
  templateItem,
}) => {
  const [documentCategoryOptions, setDocumentCategoryOptions] = useState([]);
  const initialTemplateValues = templateItem
    ? {
        ...templateItem,
        displayConfigSwitch: !!templateItem?.displayConfig,
      }
    : {
        title: "",
        description: "",
        priorityOrder: 0,
        canBeNotApplicable: false,
        isRequired: false,
        requiresPhoto: false,
        requiresVideo: false,
        areaName: "",
        stepType: StepType.General,
        module: "",
      };

  const initialDisplayConfigValues = templateItem?.displayConfig
    ? {
        title: templateItem.displayConfig.title,
        description: templateItem.displayConfig.description,
        hasButton: !!templateItem.displayConfig.buttonType,
      }
    : { title: "", description: "", hasButton: false };

  const initialButtonValues = templateItem?.displayConfig?.buttonType
    ? {
        buttonType: templateItem.displayConfig.buttonType,
        buttonLabel: templateItem.displayConfig.buttonLabel,
        content: templateItem.displayConfig.content,
      }
    : { buttonType: "", buttonLabel: "", content: "" };

  const templateForm = useForm({
    resolver: zodResolver(templateItemSchema),
    defaultValues: initialTemplateValues,
    mode: "onChange",
  });

  const {
    control: templateControl,
    watch: templateWatch,
    reset: resetTemplate,
    formState: { isValid: isTemplateValid },
    getValues: getTemplateValues,
  } = templateForm;

  const displayConfigForm = useForm({
    resolver: zodResolver(displayConfigSchema),
    defaultValues: initialDisplayConfigValues,
    mode: "onChange",
  });

  const {
    control: displayControl,
    watch: displayWatch,
    reset: resetDisplay,
    formState: { isValid: isDisplayConfigValid },
    getValues: getDisplayValues,
  } = displayConfigForm;

  const displayButtonForm = useForm({
    resolver: zodResolver(displayButtonSchema),
    defaultValues: initialButtonValues,
    mode: "onChange",
  });

  const {
    control: buttonControl,
    reset: resetButton,
    formState: { isValid: isButtonConfigValid },
    getValues: getButtonValues,
  } = displayButtonForm;

  const { getDocumentCategories } = useDocumentsViewModel();

  const stepType = templateWatch("stepType");
  const emailType = templateWatch("emailType");
  const documentCategoryId = templateWatch("documentCategoryId");
  const displayConfigSwitch = templateWatch("displayConfigSwitch");
  const hasButton = displayWatch("hasButton");

  const templateItemFields = [
    {
      label: "Title",
      name: "title",
      required: true,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Description",
      name: "description",
      required: true,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Priority Order",
      name: "priorityOrder",
      fieldType: INPUT_TYPES.NUMBER,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Area Name",
      name: "areaName",
      formControl: templateControl,
      width: "50%",
      required: true,
    },
    {
      label: "Step Type",
      name: "stepType",
      fieldType: INPUT_TYPES.SELECT,
      formControl: templateControl,
      options: Object.values(StepType).map((type) => ({
        label: type,
        value: type,
      })),
      width: "50%",
      handleChange: ({ target: { value } }) => {
        templateForm.setValue("stepType", value, {
          shouldDirty: true,
          shouldValidate: true,
        });

        if (value === StepType.Email) {
          templateForm.setValue("emailType", EmailTemplateType.OrderSand, {
            shouldValidate: true,
          });
        } else {
          templateForm.setValue("emailType", null, { shouldValidate: true });
        }

        if (value === StepType.Document) {
          templateForm.setValue(
            "documentCategoryId",
            documentCategoryOptions?.[0]?.value,
            { shouldValidate: true }
          );
        } else {
          templateForm.setValue("documentCategoryId", null, {
            shouldValidate: true,
          });
        }
      },
      required: true,
    },
    {
      label: "Email Template Type",
      name: "emailType",
      fieldType: INPUT_TYPES.SELECT,
      formControl: templateControl,
      options: [{ label: "Order Sand", value: EmailTemplateType.OrderSand }],
      width: "50%",
      disabled: stepType !== StepType.Email,
      required: stepType === StepType.Email,
    },
    {
      label: "Module",
      name: "module",
      fieldType: INPUT_TYPES.SELECT,
      formControl: templateControl,
      required: true,
      options: PLATFORM_MODULES_OPTIONS.map((module) => ({
        label: module.label,
        value: module.value,
      })),
    },
    {
      label: "Document Category",
      name: "documentCategoryId",
      fieldType: INPUT_TYPES.SELECT,
      formControl: templateControl,
      options: documentCategoryOptions,
      width: "100%",
      disabled: stepType !== StepType.Document,
      required: stepType === StepType.Document,
    },
    {
      label: "Can Be Not Applicable",
      name: "canBeNotApplicable",
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Is Required",
      name: "isRequired",
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Requires Photo",
      name: "requiresPhoto",
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Requires Video",
      name: "requiresVideo",
      fieldType: INPUT_TYPES.CHECKBOX,
      formControl: templateControl,
      width: "50%",
    },
    {
      label: "Display Config",
      name: "displayConfigSwitch",
      fieldType: INPUT_TYPES.SWITCH,
      formControl: templateControl,
      width: "100%",
    },
  ];

  const displayConfigFields = [
    {
      label: "Title",
      name: "title",
      required: true,
      formControl: displayControl,
      width: "50%",
    },
    {
      label: "Description",
      name: "description",
      required: true,
      formControl: displayControl,
      width: "50%",
    },
    {
      label: "Has Button",
      name: "hasButton",
      fieldType: INPUT_TYPES.SWITCH,
      formControl: displayControl,
      width: "50%",
    },
  ];

  const displayButtonFields = [
    {
      label: "Button Type",
      name: "buttonType",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      formControl: buttonControl,
      options: [
        { label: "Link", value: StepDisplayConfigButtonType.Link },
        {
          label: "Copy To Clipboard",
          value: StepDisplayConfigButtonType.CopyToClipboard,
        },
      ],
      width: "50%",
    },
    {
      label: "Button Label",
      name: "buttonLabel",
      required: true,
      formControl: buttonControl,
      width: "50%",
    },
    {
      label: "Content",
      name: "content",
      required: true,
      formControl: buttonControl,
      width: "50%",
    },
  ];

  const handleSubmit = async () => {
    const baseValues = getTemplateValues();

    const finalPayload = {
      ...baseValues,
      displayConfig: baseValues.displayConfigSwitch
        ? {
            ...getDisplayValues(),
            ...(getDisplayValues("hasButton") ? getButtonValues() : {}),
          }
        : null,
    };

    onSubmit(finalPayload);
    onClose();
  };

  useEffect(() => {
    if (templateItem) {
      resetTemplate(initialTemplateValues);
      resetDisplay(initialDisplayConfigValues);
      resetButton(initialButtonValues);
    }
  }, [templateItem]);

  useEffect(() => {
    const load = async () => {
      const categories = await getDocumentCategories(ENTITY_TYPES.STEP);
      setDocumentCategoryOptions(
        categories.map((c) => ({ label: c.name, value: c.id })) || []
      );
    };
    load();
  }, []);

  return (
    <MiModal
      open={open}
      closeModal={onClose}
      width={600}
      title={
        templateItem?.id
          ? `Edit ${ENTITY_NAMES.STEP_TEMPLATE}`
          : `Add ${ENTITY_NAMES.STEP_TEMPLATE}`
      }
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.GREY,
          onClick: onClose,
        },
        {
          name: "Save",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled:
            !isTemplateValid ||
            (displayConfigSwitch && !isDisplayConfigValid) ||
            (hasButton && !isButtonConfigValid) ||
            (stepType === StepType.Email && !emailType) ||
            (stepType === StepType.Document && !documentCategoryId),
          color: Colors.Primary,
          onClick: handleSubmit,
        },
      ]}
    >
      <MiDetailFields detailFields={templateItemFields} />

      <Collapse in={displayConfigSwitch}>
        <Box p={2}>
          <Typography fontWeight={600}>Display Config</Typography>
          <Divider />
        </Box>

        <MiDetailFields detailFields={displayConfigFields} />
      </Collapse>

      <Collapse in={displayConfigSwitch && hasButton}>
        <Box p={2}>
          <Typography fontWeight={600}>Display Config Button</Typography>
          <Divider />
        </Box>

        <MiDetailFields detailFields={displayButtonFields} />
      </Collapse>
    </MiModal>
  );
};
